// Enhanced Google Drive Integration Service
// This service automatically detects folders and images from your Google Drive structure

import { GOOGLE_DRIVE_CONFIG, getGoogleDriveImageUrl, extractGoogleDriveFileId } from '../data/galleryData';

class GoogleDriveService {
  constructor() {
    this.mainFolderId = GOOGLE_DRIVE_CONFIG.folderId;
    this.apiKey = null;
    this.isInitialized = false;
    this.folders = [];
    this.images = [];
  }

  // Initialize the service
  async initialize(apiKey = null) {
    try {
      this.apiKey = apiKey;
      this.isInitialized = true;
      console.log('Google Drive Service initialized');
      return true;
    } catch (error) {
      console.error('Failed to initialize Google Drive Service:', error);
      return false;
    }
  }

  // Main method: Automatically detect folders and images
  async autoDetectGalleryStructure() {
    try {
      console.log('🔍 Auto-detecting gallery structure from Google Drive...');
      
      // Step 1: Detect folders in your main gallery folder
      const detectedFolders = await this.detectFolders();
      console.log('📁 Detected folders:', detectedFolders);
      
      // Step 2: For each folder, detect images
      const allImages = [];
      for (const folder of detectedFolders) {
        const folderImages = await this.detectImagesInFolder(folder.id, folder.name);
        allImages.push(...folderImages);
      }
      
      console.log('🖼️ Total images detected:', allImages.length);
      
      return {
        folders: detectedFolders,
        images: allImages
      };
      
    } catch (error) {
      console.error('Error auto-detecting gallery structure:', error);
      // Fallback to sample data
      return this.getFallbackData();
    }
  }

  // Detect folders in your main gallery folder
  async detectFolders() {
    try {
      // Method 1: Try Google Drive API (if you have API key)
      if (this.apiKey) {
        return await this.detectFoldersWithAPI();
      }
      
      // Method 2: Try public folder access
      return await this.detectFoldersFromPublic();
      
    } catch (error) {
      console.log('Falling back to manual folder detection:', error);
      return this.getManualFolders();
    }
  }

  // Detect folders using Google Drive API
  async detectFoldersWithAPI() {
    try {
      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files?q='${this.mainFolderId}'+in+parents+and+mimeType='application/vnd.google-apps.folder'&key=${this.apiKey}&fields=files(id,name,createdTime,modifiedTime)`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

             const data = await response.json();
       return data.files.map(folder => ({
         id: folder.id,
         name: folder.name,
         category: this.generateCategoryFromFolderName(folder.name),
         icon: '',
         description: `Images from ${folder.name}`,
         createdTime: folder.createdTime,
         modifiedTime: folder.modifiedTime
       }));
      
    } catch (error) {
      console.error('Error detecting folders with API:', error);
      throw error;
    }
  }

  // Detect folders from public folder access
  async detectFoldersFromPublic() {
    try {
      console.log('Attempting to detect folders from public access...');
      
      // Since we can't directly list folders without API, we'll create a working solution
      // by using the folder structure you've already created
      return this.getWorkingFolders();
      
    } catch (error) {
      console.error('Error detecting folders from public access:', error);
      return this.getWorkingFolders();
    }
  }

  // Get working folder structure based on your actual Google Drive setup
  getWorkingFolders() {
    return [
      {
        id: 'certificates',
        name: 'Certificates',
        category: 'certificates',
        icon: '',
        description: 'Student achievements and certifications'
      },
      {
        id: 'training-sessions',
        name: 'Training Sessions',
        category: 'trainingSessions',
        icon: '',
        description: 'Live training and workshop sessions'
      },
      {
        id: 'student-projects',
        name: 'Student Projects',
        category: 'studentProjects',
        icon: '',
        description: 'BIM projects created by our students'
      },
      {
        id: 'events',
        name: 'Events',
        category: 'events',
        icon: '',
        description: 'Special events and industry meetups'
      },
      {
        id: 'workshops',
        name: 'Workshops',
        category: 'workshops',
        icon: '',
        description: 'Specialized training workshops'
      }
    ];
  }

  // Detect images in a specific folder
  async detectImagesInFolder(folderId, folderName) {
    try {
      // Method 1: Try Google Drive API
      if (this.apiKey) {
        return await this.detectImagesInFolderWithAPI(folderId, folderName);
      }
      
      // Method 2: Try to detect images from public folder access
      return await this.detectImagesFromPublicFolder(folderId, folderName);
      
    } catch (error) {
      console.error(`Error detecting images in folder ${folderName}:`, error);
      return [];
    }
  }

  // Detect images from public folder access
  async detectImagesFromPublicFolder(folderId, folderName) {
    try {
      console.log(`🔍 Attempting to detect images in ${folderName} folder...`);
      
      // Since we can't directly list files without API, we'll create a working solution
      // by using the folder structure and trying to access common image files
      return this.getWorkingImagesForFolder(folderName);
      
    } catch (error) {
      console.error(`Error detecting images from public folder ${folderName}:`, error);
      return [];
    }
  }

    // Get working images for a specific folder
  getWorkingImagesForFolder(folderName) {
    // This will be replaced with Google Sheets integration
    return [];
  }

  // Detect images using Google Drive API
  async detectImagesInFolderWithAPI(folderId, folderName) {
    try {
      const response = await fetch(
        `https://www.googleapis.com/drive/v3/files?q='${folderId}'+in+parents+and+mimeType+contains+'image/'&key=${this.apiKey}&fields=files(id,name,mimeType,size,modifiedTime,createdTime)`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.files.map(file => ({
        id: file.id,
        category: this.generateCategoryFromFolderName(folderName),
        title: this.generateTitleFromFileName(file.name),
        description: `Image from ${folderName}: ${file.name}`,
        imageUrl: getGoogleDriveImageUrl(file.id, 'full', 'export'),
        thumbnailUrl: getGoogleDriveImageUrl(file.id, 'thumbnail', 'thumbnail'),
        tags: this.generateTagsFromFileName(file.name),
        studentName: 'BimBytes Academy',
        course: folderName,
        date: file.modifiedTime ? new Date(file.modifiedTime).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        likes: 0,
        views: 0,
        // Google Drive metadata
        driveFileId: file.id,
        driveFileName: file.name,
        driveFileSize: this.formatFileSize(file.size),
        driveLastModified: file.modifiedTime ? new Date(file.modifiedTime).toLocaleString() : ''
      }));
      
    } catch (error) {
      console.error('Error detecting images with API:', error);
      throw error;
    }
  }

  // Get sample images for a specific folder
  getSampleImagesForFolder(folderName) {
    // Return empty array - we want real images from Google Drive, not placeholders
    return [];
  }

  // Generate category from folder name
  generateCategoryFromFolderName(folderName) {
    const name = folderName.toLowerCase();
    
    if (name.includes('certificate') || name.includes('cert')) return 'certificates';
    if (name.includes('training') || name.includes('session')) return 'trainingSessions';
    if (name.includes('project') || name.includes('student')) return 'studentProjects';
    if (name.includes('event')) return 'events';
    if (name.includes('workshop')) return 'workshops';
    
    return 'studentProjects'; // Default category
  }



  // Get fallback data if auto-detection fails
  getFallbackData() {
    // Return working data so gallery functions
    const folders = this.getWorkingFolders();
    const images = [];
    
    folders.forEach(folder => {
      const folderImages = this.getSampleImagesForFolder(folder.name);
      images.push(...folderImages);
    });
    
    return { folders, images };
  }

  // Legacy methods for backward compatibility
  async fetchImagesFromPublicFolder() {
    const result = await this.autoDetectGalleryStructure();
    return result.images;
  }

  async addManualImages(imageData) {
    try {
      const processedImages = imageData.map(img => ({
        id: img.id || Date.now() + Math.random(),
        category: img.category || 'studentProjects',
        title: img.title || img.fileName || 'Untitled Image',
        description: img.description || `Image from ${img.fileName}`,
        imageUrl: img.imageUrl || img.driveUrl,
        thumbnailUrl: img.thumbnailUrl || img.driveUrl,
        tags: img.tags || [],
        studentName: img.studentName || 'BimBytes Academy',
        course: img.course || 'All Courses',
        date: img.date || new Date().toISOString().split('T')[0],
        likes: img.likes || 0,
        views: img.views || 0,
        driveFileId: img.driveFileId || extractGoogleDriveFileId(img.driveUrl),
        driveFileName: img.fileName || img.title,
        driveFileSize: img.fileSize || '',
        driveLastModified: img.lastModified || ''
      }));

      return processedImages;
    } catch (error) {
      console.error('Error processing manual images:', error);
      return [];
    }
  }

  // Helper methods
  generateTitleFromFileName(fileName) {
    if (!fileName) return 'Untitled Image';
    
    const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
    
    return nameWithoutExt
      .split(/[-_\s]+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  generateTagsFromFileName(fileName) {
    if (!fileName) return [];
    
    const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
    const words = nameWithoutExt.split(/[-_\s]+/);
    
    const commonWords = ['the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by'];
    
    return words
      .filter(word => word.length > 2 && !commonWords.includes(word.toLowerCase()))
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .slice(0, 5);
  }

  formatFileSize(bytes) {
    if (!bytes) return '';
    
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  }

  // Test if an image URL is accessible
  async testImageUrl(url) {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      return response.ok;
    } catch (error) {
      return false;
    }
  }
}

// Create and export a singleton instance
const googleDriveService = new GoogleDriveService();

export default googleDriveService;
export { GoogleDriveService };
